/**
 * Value domains: the shared lists a choice field offers.
 *
 * They are shared on purpose. Two products asking "construction type" should
 * offer the same answers, and a list edited in one place is the only way that
 * stays true. Order is meaningful and is the order you see, so the list is
 * edited as a list rather than as a set.
 */
import { useState, type FormEvent } from "react";
import { ApiError, api } from "../../lib/api.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import type { DomainRow } from "../../lib/types.ts";
import { Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";

interface Draft {
  key: string;
  label: string;
  description: string;
  members: { code: string; label: string; active: boolean }[];
}

const EMPTY: Draft = { key: "", label: "", description: "", members: [{ code: "", label: "", active: true }] };

export function Domains() {
  const { can } = useAuth();
  const domains = useAsync(() => api.get<{ domains: DomainRow[] }>("/api/domains"), []);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const rows = domains.data?.domains ?? [];

  function open(row: DomainRow) {
    setEditingKey(row.key);
    setEditing({
      key: row.key,
      label: row.label,
      description: row.description ?? "",
      members: row.members.map((member) => ({ code: member.code, label: member.label, active: member.active })),
    });
  }

  if (editing) {
    return (
      <DomainForm
        draft={editing}
        existingKey={editingKey}
        onDone={() => { setEditing(null); setEditingKey(null); domains.reload(); }}
        onCancel={() => { setEditing(null); setEditingKey(null); }}
      />
    );
  }

  return (
    <div className="stack">
      <PageHead
        title="Value domains"
        sub="The lists a choice field offers. Shared, so two products asking the same question offer the same answers."
        actions={can("configuration", "edit") && (
          <button className="btn btn-primary" type="button" onClick={() => { setEditingKey(null); setEditing(EMPTY); }}>
            New domain
          </button>
        )}
      />

      <Tiles
        tiles={[
          { label: "Domains", value: rows.length, tone: "accent" },
          { label: "Values in total", value: rows.reduce((total, row) => total + row.members.length, 0), tone: "info" },
          { label: "Used by a field", value: rows.filter((row) => row.fieldCount > 0).length, tone: "ok" },
          { label: "Unused", value: rows.filter((row) => row.fieldCount === 0).length, tone: "muted" },
        ]}
      />

      <Problem error={domains.error} />

      <div className="card">
        {domains.loading && !domains.data ? (
          <Loading what="value domains" />
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>No value domains yet</h3>
            <p className="small">Create one for each coded list — states, construction types, class codes.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Domain</th><th>Key</th><th>Values</th><th>Used by</th><th /></tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      {row.label}
                      {row.description && <div className="small muted truncate" style={{ maxWidth: 340 }}>{row.description}</div>}
                    </td>
                    <td><span className="key">{row.key}</span></td>
                    <td>
                      <div className="chips">
                        {row.members.slice(0, 4).map((member) => (
                          <span key={member.code} className={`chip${member.active ? "" : " chip-warn"}`}>{member.label}</span>
                        ))}
                        {row.members.length > 4 && <span className="chip">+{row.members.length - 4} more</span>}
                      </div>
                    </td>
                    <td>{row.fieldCount || <span className="muted">—</span>}</td>
                    <td className="actions">
                      {can("configuration", "edit") && (
                        <button className="btn btn-sm" type="button" onClick={() => open(row)}>Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DomainForm({
  draft, existingKey, onDone, onCancel,
}: {
  draft: Draft;
  existingKey: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(draft);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const derivedKey = form.key.trim() || form.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  function setMember(index: number, changes: Partial<Draft["members"][number]>) {
    const members = form.members.map((member, position) => (position === index ? { ...member, ...changes } : member));
    setForm({ ...form, members });
  }

  function move(index: number, by: number) {
    const target = index + by;
    if (target < 0 || target >= form.members.length) return;
    const members = [...form.members];
    const [moved] = members.splice(index, 1);
    members.splice(target, 0, moved!);
    setForm({ ...form, members });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    const members = form.members
      .filter((member) => member.code.trim())
      .map((member) => ({ code: member.code.trim(), label: member.label.trim() || member.code.trim(), active: member.active }));

    try {
      if (existingKey) {
        await api.patch(`/api/domains/${existingKey}`, { label: form.label.trim(), description: form.description.trim() || null, members });
      } else {
        await api.post("/api/domains", { key: derivedKey, label: form.label.trim(), description: form.description.trim() || null, members });
      }
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save this domain.");
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <button type="button" className="back-link back-button" onClick={onCancel}>← Value domains</button>
      <PageHead title={existingKey ? form.label || "Value domain" : "New value domain"} />

      <form className="card" onSubmit={submit}>
        <div className="card-body stack">
          {problem && <div className="notice notice-bad">{problem}</div>}

          <div className="field-row">
            <div className="field">
              <label htmlFor="domain-label">Name<span className="required-mark">*</span></label>
              <input id="domain-label" className="control" value={form.label} required autoFocus
                placeholder="Construction type" onChange={(event) => setForm({ ...form, label: event.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="domain-key">Key</label>
              <input id="domain-key" className="control" value={existingKey ?? form.key} disabled={Boolean(existingKey)}
                placeholder={derivedKey || "construction_type"} onChange={(event) => setForm({ ...form, key: event.target.value })} />
              <span className="hint">{existingKey ? "Permanent." : "Left blank, it comes from the name."}</span>
            </div>
          </div>

          <div className="field">
            <label htmlFor="domain-description">Description</label>
            <input id="domain-description" className="control" value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </div>

          <fieldset className="fieldset">
            <legend>Values</legend>
            <p className="small muted" style={{ marginBottom: 10 }}>
              The order here is the order on the form, and it is the order comparisons will use. The code is what gets
              stored; the label is what gets read.
            </p>
            <div className="members">
              <div className="member-head small muted">
                <span>Code</span><span>Label</span><span>Offered</span><span />
              </div>
              {form.members.map((member, index) => (
                <div className="member-row" key={index}>
                  <input className="control" value={member.code} placeholder="frame"
                    onChange={(event) => setMember(index, { code: event.target.value })} />
                  <input className="control" value={member.label} placeholder="Frame"
                    onChange={(event) => setMember(index, { label: event.target.value })} />
                  <label className="checkline">
                    <input type="checkbox" checked={member.active}
                      onChange={(event) => setMember(index, { active: event.target.checked })} />
                  </label>
                  <div className="row">
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">↑</button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => move(index, 1)} disabled={index === form.members.length - 1} aria-label="Move down">↓</button>
                    <button type="button" className="btn btn-sm btn-ghost" aria-label="Remove"
                      onClick={() => setForm({ ...form, members: form.members.filter((_, position) => position !== index) })}>×</button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-sm" style={{ marginTop: 10 }}
              onClick={() => setForm({ ...form, members: [...form.members, { code: "", label: "", active: true }] })}>
              Add a value
            </button>
          </fieldset>
        </div>

        <div className="card-foot">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save domain"}</button>
        </div>
      </form>
    </div>
  );
}
